import { Sheet } from '@neo/primitives/sheet'
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react'
import { useLocation } from 'router-v6'

interface ISheetContext<T> {
  isOpen: boolean
  openSheet: (data: T) => void
  closeSheet: () => void
  sheetData: T | null
  setSheetData: React.Dispatch<React.SetStateAction<T | null>>
}

// biome-ignore lint/suspicious/noExplicitAny: one context serves every sheet's payload type; unknown fails the contravariant check on openSheet(data: T) and useSheet<T> casts on read
const SheetContext = createContext<ISheetContext<any> | undefined>(undefined)

export const SheetProvider = <T,>({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [sheetData, setSheetData] = useState<T | null>(null)

  const openSheet = (data: T) => {
    const sideSheet = document.getElementById('side-sheet')
    if (sideSheet) {
      setIsOpen(true)
      setSheetData(data)
    }
  }

  const closeSheet = () => {
    setIsOpen(false)
  }

  const location = useLocation()
  const [lastPathname, setLastPathname] = useState(location.pathname)

  // Navigating to a different page dismisses whatever sheet was open.
  //
  // This runs during render (React's sanctioned "adjust state when a prop
  // changes" pattern) rather than in an effect keyed on location.pathname,
  // because effect order made cross-page deep links impossible: a screen that
  // opens its own sheet on mount (?action=create) does so from a CHILD effect,
  // and child effects run before the parent's, so the parent's close undid the
  // open every time. Resetting in the render phase happens before the new
  // page's children mount, so their open survives. Same-pathname deep links
  // were never affected, which is why only cross-page ones looked broken.
  if (lastPathname !== location.pathname) {
    setLastPathname(location.pathname)
    setIsOpen(false)
    setSheetData(null)
  }

  const contextValue = useMemo<ISheetContext<T>>(
    () => ({ isOpen, openSheet, closeSheet, sheetData: isOpen ? sheetData : null, setSheetData }),
    [isOpen, sheetData],
  )

  return (
    <SheetContext.Provider value={contextValue}>
      <Sheet open={isOpen} onOpenChange={(open: boolean) => setIsOpen(open)}>
        {children}
      </Sheet>
    </SheetContext.Provider>
  )
}

export const useSheet = <T,>() => {
  const context = useContext(SheetContext) as ISheetContext<T>
  if (!context) {
    throw new Error('useSheet must be used within a SheetProvider')
  }
  return context
}
